type MetricType = 'counter' | 'gauge' | 'histogram'

type MetricEntry = {
  name: string
  value: number
  type: MetricType
  tags: Record<string, string | number | boolean>
  timestamp: string
  surface: 'web'
}

type LayoutShiftPerformanceEntry = PerformanceEntry & {
  hadRecentInput?: boolean
  value?: number
}

const MAX_BUFFER_SIZE = 500;
const METRICS_BUFFER: MetricEntry[] = [];
let activeFlushTimer: ReturnType<typeof setInterval> | null = null;

function resolveMetricsEndpoint() {
  const typedWindow = typeof window !== 'undefined' ? /** @type {MetricsWindow} */ (window) : null;
  if (typedWindow && typeof typedWindow.__AGENCY_METRICS_ENDPOINT__ === 'string') {
    return typedWindow.__AGENCY_METRICS_ENDPOINT__.trim();
  }

  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_METRICS_ENDPOINT) {
    return String(import.meta.env.VITE_METRICS_ENDPOINT).trim();
  }

  return '';
}

/**
 * @param {string} name
 * @param {number} value
 * @param {Record<string, string | number | boolean>} [tags]
 * @param {MetricType} [type]
 */
export function recordMetric(name: string, value: number, tags: Record<string, string | number | boolean> = {}, type: MetricType = 'gauge') {
  /** @type {MetricEntry} */
  const metric: MetricEntry = {
    name,
    value,
    type,
    tags,
    timestamp: new Date().toISOString(),
    surface: /** @type {'web'} */ ('web')
  };

  METRICS_BUFFER.push(metric);
  if (METRICS_BUFFER.length > MAX_BUFFER_SIZE) {
    METRICS_BUFFER.shift();
  }

  if (typeof window !== 'undefined') {
    const typedWindow = /** @type {MetricsWindow} */ (window);
    if (!Array.isArray(typedWindow.__AGENCY_METRICS__)) {
      typedWindow.__AGENCY_METRICS__ = [];
    }

    typedWindow.__AGENCY_METRICS__.push(metric);
  }

  return metric;
}

/** @param {string} name @param {Record<string, string | number | boolean>} [tags] */
export function incrementMetric(name: string, tags: Record<string, string | number | boolean> = {}) {
  return recordMetric(name, 1, tags, 'counter');
}

/**
 * @param {string} name
 * @param {number} durationMs
 * @param {Record<string, string | number | boolean>} [tags]
 */
export function recordDuration(name: string, durationMs: number, tags: Record<string, string | number | boolean> = {}) {
  return recordMetric(name, durationMs, tags, 'histogram');
}

export function getMetricsBuffer() {
  return [...METRICS_BUFFER];
}

export function clearMetricsBuffer() {
  METRICS_BUFFER.length = 0;
}

export async function flushMetrics() {
  const endpoint = resolveMetricsEndpoint();
  if (!endpoint || METRICS_BUFFER.length === 0) {
    return false;
  }

  const payload = {
    generatedAt: new Date().toISOString(),
    metrics: [...METRICS_BUFFER]
  };

  try {
    const body = JSON.stringify(payload);

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const queued = navigator.sendBeacon(endpoint, body);
      if (queued) {
        clearMetricsBuffer();
        return true;
      }
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body
    });

    if (!response.ok) {
      return false;
    }

    clearMetricsBuffer();
    return true;
  } catch {
    return false;
  }
}

function observePaintMetrics() {
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.name === 'first-contentful-paint') {
        recordDuration('web.paint.fcp_ms', entry.startTime);
      }
    }
  });

  try {
    observer.observe({ type: 'paint', buffered: true });
  } catch {
    return;
  }
}

function observeLargestContentfulPaint() {
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }

  const observer = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const lastEntry = entries[entries.length - 1];
    if (lastEntry) {
      recordDuration('web.paint.lcp_ms', lastEntry.startTime);
    }
  });

  try {
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {
    return;
  }
}

function observeLayoutShift() {
  if (typeof PerformanceObserver === 'undefined') {
    return;
  }

  let cls = 0;
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      const shift = entry as LayoutShiftPerformanceEntry;
      if (!shift.hadRecentInput) {
        cls += Number(shift.value || 0);
      }
    }

    recordMetric('web.layout.cls', Number(cls.toFixed(5)), {}, 'gauge');
  });

  try {
    observer.observe({ type: 'layout-shift', buffered: true });
  } catch {
    return;
  }
}

/** @param {{ flushIntervalMs?: number }} [options] */
export function initializeMetricsCollection(options: { flushIntervalMs?: number } = {}) {
  const flushIntervalMs = Number(options.flushIntervalMs || 30000);

  incrementMetric('web.metrics.initialized');
  observePaintMetrics();
  observeLargestContentfulPaint();
  observeLayoutShift();

  if (activeFlushTimer) {
    clearInterval(activeFlushTimer);
  }

  activeFlushTimer = setInterval(() => {
    void flushMetrics();
  }, flushIntervalMs);

  return () => {
    if (!activeFlushTimer) {
      return;
    }

    clearInterval(activeFlushTimer);
    activeFlushTimer = null;
  };
}
